import { useState, useEffect, useMemo } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaBell, FaChevronDown, FaBars, FaXmark } from "react-icons/fa6";
import {
  HiOutlineHome,
  HiOutlineClipboardDocumentList,
  HiOutlineEnvelope,
  HiOutlineHeart,
  HiOutlineCog6Tooth, HiOutlineChatBubbleLeftRight, HiOutlineBookOpen,
  HiOutlineBell,
  HiOutlineMap,
  HiOutlineAcademicCap,
  HiOutlineBriefcase,
  HiOutlineShieldCheck,
  HiOutlineLockClosed,
  HiOutlineQueueList,
  HiOutlineCalendarDays,
  HiOutlineUserGroup,
  HiOutlineTicket,
  HiOutlineBuildingOffice2,
  HiOutlineSparkles,
  HiOutlineShoppingBag,
  HiOutlineReceiptPercent,
  HiOutlineTableCells,
  HiOutlineSun,
  HiOutlineHomeModern,
  HiOutlineCake,
  HiOutlineArchiveBox,
  HiOutlineBanknotes,
  HiOutlineIdentification,
  HiOutlinePuzzlePiece,
  HiOutlineChevronDown,
  HiOutlineChevronDoubleLeft,
  HiOutlineChevronDoubleRight,
} from "react-icons/hi2";
import BrandMark, { makeBadgeIcon } from "./BrandMark";
import { useBrand, brandWords } from "./brand";
import CircularBadge from "../components/badge";
import { authAPI, clearToken, isLoggedIn, dashboardAPI } from "../services/api";
import { PermissionsContext, hasPermission, hasFeature } from "./permissions";
import { Toaster } from "react-hot-toast";
import "./AdminLayout.css";

/*
 * Sidebar grouped by job-to-be-done (V2.1, Page 1), each item a word + an
 * icon. Items are filtered by what this deployment does (feature) and what
 * this person may do (permission); a group with nothing left is hidden.
 * `unless`: hidden where that feature is on (Makua runs retreats through
 * Retreat Schedule, so its old Retreat Blocks and Bookings screens leave the
 * menu but keep their addresses; see App.js).
 * Names changed in V2.1 (release notes list them): Dated Retreats → Retreat
 * Schedule, Room Calendar → Booking Calendar, Kitchen → Kitchen & Dining,
 * Roles → Staff & Permissions, Settings → General Settings, Add-on Library
 * → Activities & Add-ons.
 */
export const NAV_GROUPS = [
  {
    label: null,
    items: [{ to: "/admin/dashboard", Icon: HiOutlineHome, label: "Dashboard", permission: null }], // always visible
  },
  {
    label: "People",
    items: [
      { to: "/admin/applications", Icon: HiOutlineClipboardDocumentList, label: "Applications", permission: "applications.view", feature: "applications" },
      { to: "/admin/queue", Icon: HiOutlineQueueList, label: "Queue", permission: "queue.view", feature: "queue" },
    ],
  },
  {
    label: "Retreats",
    items: [
      { to: "/admin/retreats", Icon: HiOutlineSun, label: "Retreat Schedule", permission: "blocks.view", feature: "reservations" },
      { to: "/admin/calendar", Icon: HiOutlineTableCells, label: "Booking Calendar", permission: "inventory.view", feature: "reservations" },
      { to: "/admin/reservations", Icon: HiOutlineTicket, label: "Reservations", permission: "reservations.view", feature: "reservations" },
      { to: "/admin/guests", Icon: HiOutlineIdentification, label: "Guests", permission: "reservations.view", feature: "reservations" },
      { to: "/admin/workshops", Icon: HiOutlineSparkles, label: "Workshops", permission: "workshops.view", feature: "workshops" },
      { to: "/admin/retreat-blocks", Icon: HiOutlineCalendarDays, label: "Retreat Blocks", permission: "blocks.view", feature: "retreats", unless: "reservations" },
      { to: "/admin/bookings", Icon: HiOutlineArchiveBox, label: "Bookings", permission: "blocks.view", feature: "bookings", unless: "reservations" },
      { to: "/admin/cohorts", Icon: HiOutlineUserGroup, label: "Cohorts", permission: "cohorts.view", feature: "cohorts" },
      { to: "/admin/rooms", Icon: HiOutlineBuildingOffice2, label: "Rooms & Beds", permission: "blocks.view", feature: "rooms" },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/admin/accommodation", Icon: HiOutlineHomeModern, label: "Accommodation", permission: "inventory.view", feature: "reservations" },
      { to: "/admin/activities", Icon: HiOutlinePuzzlePiece, label: "Activities & Add-ons", permission: "blocks.view", feature: "reservations" },
      { to: "/admin/kitchen", Icon: HiOutlineCake, label: "Kitchen & Dining", permission: "kitchen.view", feature: "reservations" },
    ],
  },
  {
    label: "Learning & Community",
    items: [
      { to: "/admin/course-library", Icon: HiOutlineAcademicCap, label: "Courses", permission: "courses.view", feature: "courseLibrary" },
      { to: "/admin/board", Icon: HiOutlineChatBubbleLeftRight, label: "Community", permission: "board.view", feature: "community" },
      { to: "/admin/resources", Icon: HiOutlineBookOpen, label: "Resources", permission: "resources.view", feature: "resources" },
      { to: "/admin/courses", Icon: HiOutlineAcademicCap, label: "Courses", permission: "courses.view", feature: "courses" },
      { to: "/admin/stories", Icon: HiOutlineMap, label: "Stories", permission: "stories.view", feature: "stories" },
    ],
  },
  {
    label: "Sales & Finance",
    items: [
      { to: "/admin/products", Icon: HiOutlineShoppingBag, label: "Products", permission: "shop.view", feature: "shop" },
      { to: "/admin/orders", Icon: HiOutlineReceiptPercent, label: "Orders", permission: "shop.view", feature: "shop" },
      { to: "/admin/sellers", Icon: HiOutlineBriefcase, label: "Sellers", permission: "sellers.view", feature: "reservations" },
      { to: "/admin/donations", Icon: HiOutlineHeart, label: "Donations", permission: "donations.view", feature: "donations" },
    ],
  },
  {
    label: "Communications",
    items: [
      { to: "/admin/subscribers", Icon: HiOutlineEnvelope, label: "Subscribers", permission: "subscribers.view", feature: "subscribers" },
      { to: "/admin/notifications", Icon: HiOutlineBell, label: "Notifications", permission: "notifications.view" },
    ],
  },
  {
    label: "Administration",
    items: [
      { to: "/admin/staff", Icon: HiOutlineShieldCheck, label: "Staff & Permissions", permission: "SUPER_ADMIN_ONLY" },
      { to: "/admin/booking-settings", Icon: HiOutlineBanknotes, label: "Booking Settings", permission: "settings.view", feature: "reservations" },
      { to: "/admin/settings", Icon: HiOutlineCog6Tooth, label: "General Settings", permission: "settings.view" },
    ],
  },
];

/* Remembered per browser: which groups are folded, and the icon-only rail. */
const NAV_STATE_KEY = "admin_nav_folded";
const RAIL_KEY = "admin_nav_rail";
const readJson = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch (_) { return d; } };
const writeJson = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (_) { /* private mode */ } };


export default function AdminLayout({ children, pageTitle, pageSubtitle, permission, feature }) {
  const brand = useBrand();
  const words = brandWords(brand.name);
  const badgeIcon = useMemo(() => makeBadgeIcon(brand.product, brand.short), [brand.product, brand.short]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [admin, setAdmin] = useState({ firstName: "…", lastName: "", fullName: "…", role: "admin", email: "", permissions: {}, features: null });
  /* Which product this admin is pointed at — the API says so, since the
     same admin build is deployed once per product. */
  const [product, setProduct] = useState(null);
  const [permLoading, setPermLoading] = useState(true);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [folded, setFolded] = useState(() => readJson(NAV_STATE_KEY, {}));
  const [rail, setRail] = useState(() => readJson(RAIL_KEY, false) === true);
  const toggleGroup = (label) => setFolded((f) => { const next = { ...f, [label]: !f[label] }; writeJson(NAV_STATE_KEY, next); return next; });
  // The bell: how many kinds of thing need someone (the dashboard's Needs Attention).
  const [attention, setAttention] = useState(null);
  useEffect(() => {
    if (!isLoggedIn()) return undefined;
    let alive = true;
    dashboardAPI.attention().then((r) => { if (alive && r?.data) setAttention(r.data.attention); }).catch(() => {});
    return () => { alive = false; };
  }, [pathname]);
  const attentionCount = (attention || []).length;
  const toggleRail = () => setRail((r) => { writeJson(RAIL_KEY, !r); return !r; });

  // Auth guard — redirect to login if no token
  useEffect(() => {
    if (!isLoggedIn()) {
      navigate("/admin/login");
      return;
    }
    authAPI
      .me()
      .then((res) => {
        if (res?.data) {
          const a = res.data;
          setAdmin({
            firstName: a.name.split(" ")[0],
            lastName: a.name.split(" ").slice(1).join(" "),
            fullName: a.name,
            role: a.role, // raw value: "admin" | "super_admin" — used for gating
            roleLabel: a.role === "super_admin" ? "Super Admin" : a.role_name || "Admin",
            email: a.email,
            permissions: a.permissions, // null = super_admin (all access), else {} object
            features: a.features || null, // which screens this deployment uses
          });
          setProduct(a.product || null);
          // Two-step is required and this person hasn't set it up: that comes first.
          if (a.mustSetUp2Step && window.location.pathname !== "/admin/security") navigate("/admin/security");
        }
      })
      .catch(() => {
        navigate("/admin/login");
      })
      .finally(() => setPermLoading(false));
  }, [navigate]);

  // Filter nav items the current admin is allowed to see
  const canSee = (item) => {
    // Some screens only exist for one product — Workshops is Makua's,
    // Applications and Queue are Forge's. The API says which, so one build
    // can serve both deployments.
    if (!hasFeature(admin, item.feature)) return false;
    if (item.permission === null) return true; // Dashboard — always visible
    if (item.permission === "SUPER_ADMIN_ONLY") return admin.role === "super_admin";
    return hasPermission(admin, item.permission);
  };
  const notHere = (item) => item.unless && admin.features && admin.features[item.unless] === true;
  const visibleGroups = NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => canSee(i) && !notHere(i)) })).filter((g) => g.items.length > 0);
  const isHere = (to) => pathname === to || pathname.startsWith(`${to}/`);

  // Page-level access gate — if this page requires a permission the admin lacks, show a lock screen
  const pageAllowed = permLoading
    ? null // still loading — don't flash "denied" before we know
    : !hasFeature(admin, feature)
      ? false
      : permission === "SUPER_ADMIN_ONLY"
        ? admin.role === "super_admin"
        : !permission || hasPermission(admin, permission);

  const close = () => setSidebarOpen(false);
  const signOut = () => {
    clearToken();
    navigate("/admin/login");
    close();
  };

  return (
    <div className={`al ${rail ? "al--rail" : ""}`}>
      {/* ── Sidebar ── */}
      <aside className={`al__sidebar ${sidebarOpen ? "al__sidebar--open" : ""}`}>
        {/* Logo */}
        <Link to="/admin/dashboard" className="al__logo" onClick={close} aria-label={`${brand.name} dashboard`}>
          <BrandMark product={brand.product} short={brand.short} fill="var(--admin-rust)" width={38} height={38} />
          <div className="al__logo-words">
            <span className="al__logo-name">{words[0]}</span>
            {words.slice(1).map((w) => <span key={w} className="al__logo-sub">{w}</span>)}
          </div>
        </Link>

        {/* Nav: scrolls on its own, so a short window never hides the last links */}
        <nav className="al__nav" aria-label="Admin navigation">
          {visibleGroups.map((group) => {
            const id = `nav-${(group.label || "main").toLowerCase().replace(/[^a-z]+/g, "-")}`;
            // The group holding the page you're on is always open.
            const holdsActive = group.items.some((i) => isHere(i.to));
            const open = !group.label || rail || holdsActive || !folded[group.label];
            return (
              <div key={group.label || "main"} className={`al__nav-group ${open ? "" : "is-folded"}`}>
                {group.label && (
                  <button type="button" className="al__nav-group-btn" aria-expanded={open} aria-controls={id}
                    onClick={() => toggleGroup(group.label)} disabled={holdsActive && !rail} title={holdsActive ? "The page you're on is in this group" : undefined}>
                    <HiOutlineChevronDown className="al__nav-group-chev" aria-hidden="true" />
                    <span>{group.label}</span>
                  </button>
                )}
                <div id={id} className="al__nav-items" hidden={!open}>
                  {group.items.map(({ to, Icon, label }) => (
                    <NavLink key={to} to={to} className={({ isActive }) => `al__nav-link ${isActive || isHere(to) ? "al__nav-link--active" : ""}`}
                      onClick={close} title={rail ? label : undefined} aria-label={rail ? label : undefined}>
                      <Icon className="al__nav-icon" aria-hidden="true" />
                      <span className="al__nav-text">{label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
          {/* Circular badge */}
          <div className="al__sidebar-badge" aria-hidden="true">
            <CircularBadge text={`${brand.tagline} ·`} colors="rgba(255,255,255,0.3)" icon={badgeIcon} />
          </div>
        </nav>

        <button type="button" className="al__rail-btn" onClick={toggleRail} aria-pressed={rail}
          aria-label={rail ? "Expand sidebar" : "Collapse sidebar to icons"} title={rail ? "Expand sidebar" : "Collapse sidebar"}>
          {rail ? <HiOutlineChevronDoubleRight aria-hidden="true" /> : <HiOutlineChevronDoubleLeft aria-hidden="true" />}
          <span className="al__nav-text">Collapse sidebar</span>
        </button>
      </aside>

      {sidebarOpen && <div className="al__backdrop" onClick={close} aria-hidden="true" />}

      {/* ── Main ── */}
      <div className="al__main">
        {/* Topbar */}
        <header className="al__topbar">
          <button type="button" className="al__topbar-menu" onClick={() => setSidebarOpen((v) => !v)} aria-label="Toggle sidebar">
            {sidebarOpen ? <FaXmark /> : <FaBars />}
          </button>

          {/* Spacer */}
          <div className="al__topbar-spacer" />

          {/* Bell */}
          <Link to="/admin/dashboard#attention" className="al__topbar-bell"
            aria-label={attentionCount ? `${attentionCount} ${attentionCount === 1 ? "thing needs" : "things need"} attention` : "Nothing needs attention"}
            title={attention ? attention.map((a) => `${a.count} ${a.label}`).join("\n") || "Nothing needs attention" : undefined}>
            <FaBell />
            {attentionCount > 0 && <span className="al__bell-badge">{attentionCount}</span>}
          </Link>

          {/* User */}
          <div className="al__topbar-user">
            <button type="button" className="al__topbar-user-btn" onClick={() => setProfileOpen((v) => !v)}>
              <div className="al__topbar-avatar">
                {admin.firstName[0]}
                {admin.lastName[0]}
              </div>
              <div className="al__topbar-user-info">
                <span className="al__topbar-user-name">{admin.fullName}</span>
                <span className="al__topbar-user-role">{admin.roleLabel}</span>
              </div>
              <FaChevronDown className="al__topbar-chevron" style={{ transform: profileOpen ? "rotate(180deg)" : "rotate(0)" }} />
            </button>

            <AnimatePresence>
              {profileOpen && (
                <motion.div className="al__profile-dropdown" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.18 }}>
                  <div className="al__dd-top">
                    <div className="al__topbar-avatar al__topbar-avatar--lg">
                      {admin.firstName[0]}
                      {admin.lastName[0]}
                    </div>
                    <div>
                      <p className="al__dd-name">{admin.fullName}</p>
                      <p className="al__dd-email">{admin.email}</p>
                    </div>
                  </div>
                  {hasPermission(admin, "settings.view") && (
                    <Link to="/admin/settings" className="al__dd-link" onClick={() => setProfileOpen(false)}>
                      General Settings
                    </Link>
                  )}
                  <Link to="/admin/security" className="al__dd-link" onClick={() => setProfileOpen(false)}>
                    Security (two-step sign-in)
                  </Link>
                  <div className="al__dd-divider" />
                  <button type="button" className="al__dd-signout" onClick={signOut}>
                    Sign Out
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </header>

        {/* Global toast notifications */}
        <Toaster
          position="top-right"
          gutter={10}
          toastOptions={{
            duration: 4000,
            style: {
              background: "#fff",
              color: "#1b1b1b",
              border: "1px solid #ddd5c8",
              borderRadius: "8px",
              fontSize: "0.84rem",
              fontWeight: "500",
              boxShadow: "0 4px 20px rgba(27,27,27,0.12)",
              maxWidth: "380px",
              padding: "0.75rem 1rem",
            },
            success: {
              iconTheme: { primary: "#16a34a", secondary: "#fff" },
              style: { borderLeft: "3px solid #16a34a" },
            },
            error: {
              iconTheme: { primary: "#dc2626", secondary: "#fff" },
              style: { borderLeft: "3px solid #dc2626" },
              duration: 6000,
            },
            loading: {
              iconTheme: { primary: "#e05a2b", secondary: "#fff" },
              style: { borderLeft: "3px solid #e05a2b" },
            },
          }}
        />

        {/* Page content */}
        <main className="al__content">
          {/* Page title */}
          <div className="al__page-head">
            <h1 className="al__page-title">{pageTitle}</h1>
            {pageSubtitle && <p className="al__page-sub">{pageSubtitle}</p>}
          </div>
          {pageAllowed === null ? (
            <div className="al__access-loading">Loading…</div>
          ) : pageAllowed === false ? (
            <div className="al__access-denied">
              <HiOutlineLockClosed className="al__access-denied-icon" />
              {/* A screen this product doesn't have isn't a permissions
                  problem, and telling someone to go find a super admin
                  about it would send them on a wild goose chase. */}
              {!hasFeature(admin, feature) ? (
                <>
                  <p className="al__access-denied-title">Not part of this site</p>
                  <p className="al__access-denied-sub">
                    This section belongs to the other site running on this system, so there's nothing to show here.
                  </p>
                </>
              ) : (
                <>
                  <p className="al__access-denied-title">Access Restricted</p>
                  <p className="al__access-denied-sub">Your permission role doesn't include access to this section. Contact a super admin if you believe this is a mistake.</p>
                </>
              )}
            </div>
          ) : (
            <PermissionsContext.Provider value={{ role: admin.role, permissions: admin.permissions, loading: permLoading, admin, product }}>{children}</PermissionsContext.Provider>
          )}
        </main>
      </div>
    </div>
  );
}
